/**
 * V2OperatorService
 *
 * Thin wrapper over v2-variable-utils VariableOperatorService,
 * providing condition-editor-specific conveniences.
 *
 * Delegates all operator logic to the SSoT in v2-variable-utils.
 *
 * @module v2-condition-editor
 */

import type { ComparisonOperator, VariableType } from '@segmently/schema-models/v2';
import type { V2DataSource } from '../types/v2-condition-editor.types';
import type { V2OperatorInfo } from '@modules/v2-variable-utils/types/variable-utils.types';
import { variableOperatorService } from '@modules/v2-variable-utils/services/VariableOperatorService';

export class V2OperatorService {
  /**
   * Get available operators for a data source.
   * Delegates to VariableOperatorService using the variable's type.
   */
  getOperatorsForDataSource(dataSource: V2DataSource): V2OperatorInfo[] {
    return variableOperatorService.getOperatorsForType(dataSource.variableType);
  }

  /**
   * Get available operators for a variable type.
   */
  getOperatorsForType(variableType: VariableType): V2OperatorInfo[] {
    return variableOperatorService.getOperatorsForType(variableType);
  }

  /**
   * Check if an operator is valid for a given variable type.
   */
  isOperatorValid(
    operator: ComparisonOperator | string,
    variableType: VariableType,
  ): boolean {
    return variableOperatorService.isOperatorValid(operator as ComparisonOperator, variableType);
  }

  /**
   * Check if an operator requires a value (false for exists/notExists).
   */
  operatorRequiresValue(operator: ComparisonOperator | string): boolean {
    return variableOperatorService.operatorRequiresValue(operator as ComparisonOperator);
  }

  /**
   * Check if an operator requires multi-value input (in/notIn/containsAll/containsAny).
   */
  operatorRequiresMultiValue(operator: ComparisonOperator | string): boolean {
    return variableOperatorService.operatorRequiresMultiValue(operator as ComparisonOperator);
  }

  /**
   * Get the default operator for a variable type.
   * Returns 'eq' as a sensible default for all types.
   */
  getDefaultOperator(variableType: VariableType): ComparisonOperator {
    return 'eq' as ComparisonOperator;
  }
}

/** Singleton instance */
export const v2OperatorService = new V2OperatorService();
